import { defineComponent, h } from 'vue'
import { QTooltip } from 'quasar'

import BsBtn from '../btn/BsBtn'
import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'

/**
 * The little question-mark button that shows an explanation on hover. Renders as a BsBtn whose only
 * content is the tooltip, which is why the tooltip goes in through a scoped slot.
 *
 * `tooltipWithPointerEvents` lets the reader move the mouse into the tip — needed when it holds a link.
 */
export default defineComponent({
  name: 'BsTooltip',

  props: {
    ...bsComponentProps,
    text: { type: String, default: void 0 },
    tooltipOffset: { type: Array as unknown as () => number[], default: void 0 },
    tooltipOffsetY: { type: Number, default: void 0 },
    tooltipAnchor: { type: String, default: void 0 },
    tooltipSelf: { type: String, default: void 0 },
    tooltipHideDelay: { type: Number, default: void 0 },
    tooltipWithPointerEvents: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    return { ...useBsComponent({ vm: useVm(), props, emit }) }
  },

  methods: {
    /** Renamed from `__renderTooltip`: Vue does not proxy keys beginning with `_`. */
    renderTooltip () {
      return h(QTooltip, {
        // `class`, not `contentClass`: Quasar 2 dropped that prop from QTooltip, and puts a
        // fall-through class on the same `.q-tooltip` element the old prop reached.
        class: {
            'bs-tooltip__tip': true,
            'bs-tooltip__tip--with-pointer-events': this.tooltipWithPointerEvents
          },
        offset: this.tooltipOffset || [-4, (this.tooltipOffsetY || 0) + 30],
        anchor: this.tooltipAnchor || 'bottom left',
        self: this.tooltipSelf || 'center left',
        hideDelay: this.tooltipHideDelay
      // QTooltip is a component, so its body is a slot function rather than a children array.
      }, {
        // Filtered, because an absent slot yields undefined and Vue 2 dropped it from the children
        // array by itself -- Vue 3 keeps it, which shows up as an extra empty child.
        default: () => [
          this.text,
          this.slot(this, 'default')
        ].filter(child => child !== void 0)
      })
    }
  },

  render () {
    const props = {
      icon: 'app:question-circle',
      size: '.9375rem',
      color: 'transparent',
      textColor: 'bs-g',
      iconOnly: true
    }

    const scopedSlots = {
      default: () => this.renderTooltip()
    }

    return h(BsBtn, {
      class: 'bs-tooltip',
      ...this.getRenderProps(props),
      ...quasarHandlers(this.getListeners())
    }, this.getSlots(scopedSlots))
  }
})
